'use client'

import type { ComponentProps, ReactNode } from 'react'
import { clsx } from 'clsx'
import { isInvalid } from '@/ui/Field'
import { ExclamationCircleIcon } from '@/ui/icons/ExclamationCircleIcon'

type Props = ComponentProps<'input'> & {
  /** A 24px control at the end of the field, before the error icon - e.g. a clear button. */
  trailing?: ReactNode
  /** Drops what the field does not take as it is typed, pasted or autofilled. Must work character by character. */
  filter?: (value: string) => string
}

export function Input({ className, trailing, filter, onChange, ...props }: Props) {
  const invalid = isInvalid(props['aria-invalid'])

  return (
    <span className="relative block">
      <input
        className={clsx(
          'h-9 w-full rounded-sm border border-neutral-400 bg-white px-2.5 text-field text-neutral-800',
          'placeholder:text-placeholder placeholder:text-neutral-400',
          'focus-visible:focus-ring',
          'aria-invalid:border-error-600 aria-invalid:text-error-500',
          trailing ? (invalid ? 'pe-15' : 'pe-8') : invalid && 'pe-9',
          'disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400',
          className
        )}
        onChange={(event) => {
          if (filter) keepFiltered(event.currentTarget, filter)
          onChange?.(event)
        }}
        {...props}
      />
      {trailing ? (
        <span className={clsx('absolute top-1/2 flex -translate-y-1/2', invalid ? 'inset-e-8' : 'inset-e-1.5')}>
          {trailing}
        </span>
      ) : null}
      {invalid ? (
        <ExclamationCircleIcon className="pointer-events-none absolute inset-e-2.5 top-1/2 w-5 -translate-y-1/2 text-error-500" />
      ) : null}
    </span>
  )
}

function keepFiltered(input: HTMLInputElement, filter: (value: string) => string) {
  const { value, selectionStart } = input
  const kept = filter(value)
  if (kept === value) return
  // Setting the value moves the caret to the end; put it back after what was kept before it.
  const caret = filter(value.slice(0, selectionStart ?? value.length)).length
  input.value = kept
  input.setSelectionRange(caret, caret)
}
